import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FloaterTooltipMarkdown,
  Icon,
  Copy,
  Checkbox,
  Label,
  Optional,
  useMediaQueries,
} from '@honda-canada/design-system-react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { useTranslation } from 'react-i18next';
import themeStyles from './styles/PricingDetails.styles';
import SaveLabel from './SaveLabel';

const Container = themeStyles.apply(Box, 'Container');
const PriceContainer = themeStyles.apply(Box, 'PriceContainer');
const PriceCopy = themeStyles.apply(Copy, 'PriceCopy');
const PriceDiscountCopy = themeStyles.apply(Copy, 'PriceDiscountCopy');
const CheckboxContainer = themeStyles.apply(Box, 'CheckboxContainer');
const PriceToolTipContainer = themeStyles.apply(Box, 'PriceToolTipContainer');
const Tooltip = themeStyles.apply(FloaterTooltipMarkdown, 'Tooltip');
const TooltipLabel = themeStyles.apply(Copy, 'TooltipLabel');
const CheckboxLabel = themeStyles.apply(Label, 'CheckboxLabel');
const PriceCopyContainer = themeStyles.apply(Label, 'PriceCopyContainer');
const PriceDisclaimerLabel = themeStyles.apply(Copy, 'PriceDisclaimerLabel');

const Price = ({
  priceLabel,
  withTaxToolTipLabel,
  noTaxToolTipLabel,
  price,
  includesSalesTax,
  setIncludesSalesTax,
  includesSalesTaxLabel,
  hasDiscount,
  totalDiscount,
  discountedPrice,
  noDiscountPrice,
  isSellingProvince,
  frieghtPDIFeesCost,
  ...rest
}) => {
  const { sitecoreContext } = useSitecoreContext() || {};
  const language = sitecoreContext?.language || 'en';
  const { t } = useTranslation();
  const { isMobile } = useMediaQueries();

  const freightAmount = formatPrice(frieghtPDIFeesCost, language, 2);

  const sellingPriceDisclaimerLabel =
    isSellingProvince && includesSalesTax
      ? t('Shared.Common.sellingPriceDisclaimerWithTaxLabel', { freightAmount })
      : t('Shared.Common.sellingPriceDisclaimerNoTaxLabel', { freightAmount });

  return (
    <Container {...rest}>
      <Optional when={hasDiscount}>
        <SaveLabel value={totalDiscount} />
      </Optional>
      <PriceContainer hasDiscount={hasDiscount}>
        <Box>
          <PriceToolTipContainer>
            <TooltipLabel data-testid="cy-pricing-label">{priceLabel}</TooltipLabel>
            <Tooltip
              content={includesSalesTax ? withTaxToolTipLabel : noTaxToolTipLabel}
              data-testid="cy-pricing-tooltip"
            >
              {({ active }) => <Icon name="information" filled={active} />}
            </Tooltip>
          </PriceToolTipContainer>
          <PriceCopyContainer isSellingProvince={isSellingProvince}>
            <PriceCopy data-testid="cy-pricing-price" hasDiscount={hasDiscount}>
              {formatPrice(hasDiscount ? discountedPrice : price, language, 2)}
            </PriceCopy>
            <Optional when={hasDiscount}>
              <PriceDiscountCopy data-testid="cy-pricing-without-discount">
                {formatPrice(noDiscountPrice, language, 2)}
              </PriceDiscountCopy>
            </Optional>
          </PriceCopyContainer>
        </Box>
        <Optional when={isSellingProvince && isMobile}>
          <PriceDisclaimerLabel data-testid="cy-pricing-disclaimer">{sellingPriceDisclaimerLabel}</PriceDisclaimerLabel>
        </Optional>
        <CheckboxContainer data-testid="cy-includesSalesTax-checkbox">
          <Checkbox
            containerSize="small"
            name="includesSalesTax"
            id="includesSalesTax"
            checked={includesSalesTax}
            onClick={() => setIncludesSalesTax({ includesSalesTax: !includesSalesTax })}
          />
          <CheckboxLabel htmlFor="includesSalesTax" data-testid="cy-includesSalesTax-checkbox-label">
            {includesSalesTaxLabel}
          </CheckboxLabel>
        </CheckboxContainer>
      </PriceContainer>
      <Optional when={isSellingProvince && !isMobile}>
        <PriceDisclaimerLabel data-testid="cy-pricing-disclaimer">{sellingPriceDisclaimerLabel}</PriceDisclaimerLabel>
      </Optional>
    </Container>
  );
};

Price.propTypes = {
  priceLabel: PropTypes.string,
  price: PropTypes.number,
  withTaxToolTipLabel: PropTypes.string,
  noTaxToolTipLabel: PropTypes.string,
  includesSalesTaxLabel: PropTypes.string,
  includesSalesTax: PropTypes.bool,
  setIncludesSalesTax: PropTypes.func,
  hasDiscount: PropTypes.bool,
  totalDiscount: PropTypes.number,
  discountedPrice: PropTypes.number,
  noDiscountPrice: PropTypes.number,
  isSellingProvince: PropTypes.bool,
  frieghtPDIFeesCost: PropTypes.number,
};

export default Price;
